import { api } from '../api';
import { isAdmin, navigate } from '../app';
import { esc, $, setHTML, formatCurrency, formatDate, icons } from '../helpers';
import { toast, openModal, closeModal, confirmDialog, setBreadcrumb } from '../ui';

const CATEGORIES = [
  'Software & Subscriptions',
  'Hosting & Infrastructure',
  'Professional Services',
  'Advertising & Marketing',
  'Office Expenses',
  'Bank & Processing Fees',
  'Contract Labor',
  'Equipment & Hardware',
  'Travel & Meals',
  'Education & Training',
  'Other',
];

const INCOME_SOURCES = ['App Store', 'Google Play', 'Stripe', 'Ad Revenue', 'Other'];

let activeTab: 'summary' | 'income' | 'expenses' | 'capital' = 'summary';

export async function renderFinance() {
  if (!isAdmin()) return navigate('/dashboard');
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Finance' }]);

  try {
    const year = new Date().getFullYear();
    const [summary, incomeData, expensesData, capitalData] = await Promise.all([
      api.get('/finance/summary?year=' + year),
      api.get('/income'),
      api.get('/expenses'),
      api.get('/capital'),
    ]);
    const income = incomeData.income || [];
    const receipts = incomeData.receipts || [];
    const expenses = expensesData.expenses || [];
    const capital = capitalData.capital || [];

    setHTML(content, '<div class="page-enter">'
      + '<div class="page-header"><h1>Finance</h1><div class="page-header-actions">'
      + '<button class="btn btn-secondary" id="fin-add-income"><i data-lucide="trending-up"></i> Add Income</button>'
      + '<button class="btn btn-secondary" id="fin-add-expense"><i data-lucide="receipt"></i> Add Expense</button>'
      + '<button class="btn btn-primary" id="fin-add-capital"><i data-lucide="landmark"></i> Add Capital</button>'
      + '</div></div>'
      + '<div class="tabs" style="display:flex;gap:0.4rem;margin-bottom:1.2rem;border-bottom:1px solid var(--border)">'
      + tabBtn('summary', 'Summary') + tabBtn('income', 'Income (' + (income.length + receipts.length) + ')') + tabBtn('expenses', 'Expenses (' + expenses.length + ')') + tabBtn('capital', 'Capital (' + capital.length + ')')
      + '</div>'
      + '<div id="fin-summary" style="display:' + (activeTab === 'summary' ? 'block' : 'none') + '">' + summaryHtml(summary) + '</div>'
      + '<div id="fin-income" style="display:' + (activeTab === 'income' ? 'block' : 'none') + '">' + incomeHtml(income, receipts) + '</div>'
      + '<div id="fin-expenses" style="display:' + (activeTab === 'expenses' ? 'block' : 'none') + '">' + expensesHtml(expenses) + '</div>'
      + '<div id="fin-capital" style="display:' + (activeTab === 'capital' ? 'block' : 'none') + '">' + capitalHtml(capital) + '</div>'
      + '</div>');
    icons();

    document.querySelectorAll('[data-fin-tab]').forEach(el => el.addEventListener('click', () => {
      activeTab = el.getAttribute('data-fin-tab') as any;
      renderFinance();
    }));
    document.getElementById('fin-add-income')?.addEventListener('click', () => openIncomeForm());
    document.getElementById('fin-add-expense')?.addEventListener('click', () => openExpenseForm());
    document.getElementById('fin-add-capital')?.addEventListener('click', () => openCapitalForm());
    document.querySelectorAll('[data-edit-inc]').forEach(el => el.addEventListener('click', () => {
      openIncomeForm(income.find((i: any) => i.id === parseInt(el.getAttribute('data-edit-inc')!)));
    }));
    document.querySelectorAll('[data-del-inc]').forEach(el => el.addEventListener('click', async () => {
      if (await confirmDialog('Delete Income', 'Delete this income entry?', 'Delete', 'danger')) {
        try { await api.del('/income/' + el.getAttribute('data-del-inc')); toast('Income deleted', 'success'); renderFinance(); }
        catch (err: any) { toast(err.message, 'error'); }
      }
    }));
    document.querySelectorAll('[data-edit-exp]').forEach(el => el.addEventListener('click', () => {
      openExpenseForm(expenses.find((e: any) => e.id === parseInt(el.getAttribute('data-edit-exp')!)));
    }));
    document.querySelectorAll('[data-del-exp]').forEach(el => el.addEventListener('click', async () => {
      if (await confirmDialog('Delete Expense', 'Delete this expense? If it was personally paid, its linked capital entry is removed too.', 'Delete', 'danger')) {
        try { await api.del('/expenses/' + el.getAttribute('data-del-exp')); toast('Expense deleted', 'success'); renderFinance(); }
        catch (err: any) { toast(err.message, 'error'); }
      }
    }));
    document.querySelectorAll('[data-del-cap]').forEach(el => el.addEventListener('click', async () => {
      if (await confirmDialog('Delete Capital Entry', 'Delete this capital entry?', 'Delete', 'danger')) {
        try { await api.del('/capital/' + el.getAttribute('data-del-cap')); toast('Capital entry deleted', 'success'); renderFinance(); }
        catch (err: any) { toast(err.message, 'error'); }
      }
    }));
  } catch (err: any) { toast(err.message, 'error'); }
}

function tabBtn(key: string, label: string) {
  const on = activeTab === key;
  return '<button data-fin-tab="' + key + '" style="padding:0.6rem 1rem;background:none;border:none;border-bottom:2px solid ' + (on ? 'var(--accent)' : 'transparent') + ';color:' + (on ? 'var(--text-primary)' : 'var(--text-muted)') + ';font-weight:' + (on ? '600' : '400') + ';cursor:pointer;font-size:0.92rem">' + esc(label) + '</button>';
}

function card(label: string, value: string, color?: string) {
  return '<div class="card" style="flex:1;min-width:160px"><div class="card-body">'
    + '<div style="font-size:0.78rem;color:var(--text-muted);margin-bottom:0.3rem">' + esc(label) + '</div>'
    + '<div style="font-size:1.5rem;font-weight:700;font-family:var(--font-heading)' + (color ? ';color:' + color : '') + '">' + esc(value) + '</div>'
    + '</div></div>';
}

function summaryHtml(s: any) {
  const net = s.net_profit || 0;
  let catRows = '';
  (s.expenses_by_category || []).forEach((r: any) => {
    catRows += '<tr><td>' + esc(r.category) + '</td><td class="text-right">' + formatCurrency(r.total) + '</td></tr>';
  });
  let srcRows = '';
  (s.income_by_source || []).forEach((r: any) => {
    srcRows += '<tr><td>' + esc(r.source) + '</td><td class="text-right">' + formatCurrency(r.total) + '</td><td class="text-right">' + formatCurrency(r.fees) + '</td></tr>';
  });
  return '<div style="display:flex;gap:0.8rem;flex-wrap:wrap;margin-bottom:0.4rem">'
    + card('Income (' + s.year + ')', formatCurrency(s.income))
    + card('Deductible Expenses', formatCurrency(s.expenses))
    + card('Net Profit', formatCurrency(net), net >= 0 ? 'var(--success)' : 'var(--danger)')
    + card('Owner Equity (lifetime)', formatCurrency(s.owner_equity))
    + '</div>'
    + '<p style="font-size:0.82rem;color:var(--text-muted);margin:0.4rem 0 1.2rem">Income = invoices ' + formatCurrency(s.invoice_income) + ' + other ' + formatCurrency(s.other_income) + '. Expenses include platform fees ' + formatCurrency(s.platform_fees) + ' (deductible).</p>'
    + '<div style="display:flex;gap:0.8rem;flex-wrap:wrap;margin-bottom:1.2rem">'
    + card('Capital Contributions (' + s.year + ')', formatCurrency(s.capital_contributions))
    + card('Owner Draws (' + s.year + ')', formatCurrency(s.capital_draws))
    + '</div>'
    + (srcRows ? '<div class="card mb-2"><div class="card-header"><h3>Income by Source (' + s.year + ')</h3></div><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr><th>Source</th><th class="text-right">Gross</th><th class="text-right">Platform Fee</th></tr></thead><tbody>' + srcRows + '</tbody></table></div></div></div>' : '')
    + '<div class="card"><div class="card-header"><h3>Expenses by Category (' + s.year + ')</h3></div><div class="card-body no-pad">'
    + (catRows ? '<div class="table-wrapper"><table><thead><tr><th>Category</th><th class="text-right">Total</th></tr></thead><tbody>' + catRows + '</tbody></table></div>' : '<div class="empty-state" style="padding:1.5rem"><p>No expenses yet</p></div>')
    + '</div></div>'
    + '<p style="font-size:0.78rem;color:var(--text-muted);margin-top:0.8rem">Income = paid invoices + other income (gross). Net profit = income − deductible expenses (incl. platform fees). Owner equity = lifetime contributions − draws (capital, not income).</p>';
}

function receiptsHtml(receipts: any[]) {
  if (!receipts.length) return '';
  let rows = '';
  receipts.forEach((p: any) => {
    const gross = p.gross || 0;
    const fee = p.fee || 0;
    const net = p.net != null ? p.net : gross - fee;
    let method: string;
    if (p.payment_provider === 'stripe') {
      method = 'Stripe' + (p.card_brand ? ' <span class="text-muted" style="font-size:0.78rem">· ' + esc(p.card_brand) + (p.card_last4 ? ' ····' + esc(p.card_last4) : '') + '</span>' : '');
    } else if (p.payment_provider === 'crypto') {
      method = 'Crypto';
    } else {
      method = esc(p.payment_provider || '—');
    }
    rows += '<tr>'
      + '<td>' + formatDate(p.confirmed_at) + '</td>'
      + '<td>' + esc(p.client_name || '—') + '</td>'
      + '<td>' + esc(p.invoice_number || '—') + '</td>'
      + '<td>' + method + '</td>'
      + '<td class="text-right">' + formatCurrency(gross, p.currency) + '</td>'
      + '<td class="text-right">' + (fee ? formatCurrency(fee, p.currency) : '—') + '</td>'
      + '<td class="text-right">' + formatCurrency(net, p.currency) + '</td>'
      + '</tr>';
  });
  return '<div class="card mb-2"><div class="card-header"><h3>Invoice Payments Received</h3></div><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr>'
    + '<th>Date</th><th>Client</th><th>Invoice</th><th>Method</th><th class="text-right">Gross</th><th class="text-right">Fee</th><th class="text-right">Net</th>'
    + '</tr></thead><tbody>' + rows + '</tbody></table></div>'
    + '<div class="card-body" style="padding-top:0.6rem"><p style="font-size:0.78rem;color:var(--text-muted);margin:0">Auto-recorded from paid invoices. Gross is booked as income; the processing fee is a deductible expense (Bank &amp; Processing Fees). Read-only — driven by actual payments.</p></div></div></div>';
}

function incomeHtml(income: any[], receipts: any[]) {
  let rows = '';
  income.forEach((i: any) => {
    const netAmt = (i.amount || 0) - (i.fee || 0);
    rows += '<tr>'
      + '<td>' + formatDate(i.date) + '</td>'
      + '<td>' + esc(i.source) + (i.period ? ' <span class="text-muted" style="font-size:0.78rem">(' + esc(i.period) + ')</span>' : '') + '</td>'
      + '<td class="text-right">' + formatCurrency(i.amount, i.currency) + '</td>'
      + '<td class="text-right">' + (i.fee ? formatCurrency(i.fee, i.currency) : '—') + '</td>'
      + '<td class="text-right">' + formatCurrency(netAmt, i.currency) + '</td>'
      + '<td class="row-actions"><button class="btn btn-ghost btn-sm" data-edit-inc="' + i.id + '"><i data-lucide="pencil"></i></button>'
      + '<button class="btn btn-ghost btn-sm" data-del-inc="' + i.id + '"><i data-lucide="trash-2"></i></button></td>'
      + '</tr>';
  });
  const entriesTable = income.length
    ? '<div class="table-wrapper"><table><thead><tr>'
      + '<th>Date</th><th>Source</th><th class="text-right">Gross</th><th class="text-right">Platform Fee</th><th class="text-right">Net</th><th>Actions</th>'
      + '</tr></thead><tbody>' + rows + '</tbody></table></div>'
    : '<div class="empty-state" style="padding:1.5rem"><p>No manual income entries. Use “Add Income” for App Store, ad revenue, and other non-invoice income.</p></div>';
  return receiptsHtml(receipts)
    + '<div class="card"><div class="card-header"><h3>Other Income</h3></div><div class="card-body no-pad">' + entriesTable + '</div></div>'
    + '<p style="font-size:0.78rem;color:var(--text-muted);margin-top:0.8rem">Gross is reported as income; the platform fee (Apple/Google/Stripe cut) is counted as a deductible expense. Net is what actually lands in your account.</p>';
}

function openIncomeForm(entry?: any) {
  const isEdit = !!entry;
  const today = new Date().toISOString().split('T')[0];
  const srcOpts = INCOME_SOURCES.map(s => '<option value="' + esc(s) + '"' + (entry?.source === s ? ' selected' : '') + '>' + esc(s) + '</option>').join('');
  openModal(isEdit ? 'Edit Income' : 'New Income',
    '<form id="income-form">'
    + '<div class="form-row"><div class="form-group"><label>Date *</label><input type="date" name="date" value="' + esc(entry?.date?.split('T')[0] || today) + '" required></div>'
    + '<div class="form-group"><label>Source *</label><select name="source" required>' + srcOpts + '</select></div></div>'
    + '<div class="form-row"><div class="form-group"><label>Gross Amount (USD) *</label><input type="number" name="amount" step="0.01" min="0.01" value="' + esc(entry?.amount ?? '') + '" required placeholder="What you earned before the platform cut"></div>'
    + '<div class="form-group"><label>Platform Fee (USD)</label><input type="number" name="fee" step="0.01" min="0" value="' + esc(entry?.fee ?? '') + '" placeholder="Apple/Google commission"></div></div>'
    + '<div class="form-row"><div class="form-group"><label>Period</label><input type="text" name="period" value="' + esc(entry?.period || '') + '" placeholder="e.g. May 2026"></div>'
    + '<div class="form-group"><label>Description</label><input type="text" name="description" value="' + esc(entry?.description || '') + '"></div></div>'
    + '<div class="form-group"><label>Notes</label><textarea name="notes" rows="2">' + esc(entry?.notes || '') + '</textarea></div>'
    + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="inc-cancel">Cancel</button>'
    + '<button type="submit" class="btn btn-primary">' + (isEdit ? 'Save' : 'Add Income') + '</button></div></form>');
  document.getElementById('inc-cancel')?.addEventListener('click', closeModal);
  document.getElementById('income-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    body.amount = parseFloat(body.amount);
    body.fee = parseFloat(body.fee) || 0;
    body.currency = 'USD';
    try {
      if (isEdit) { await api.put('/income/' + entry.id, body); toast('Income updated', 'success'); }
      else { await api.post('/income', body); toast('Income added', 'success'); }
      closeModal(); renderFinance();
    } catch (err: any) { toast(err.message, 'error'); }
  });
}

function srcBadge(src: string) {
  const personal = src === 'personal';
  return '<span class="status status-' + (personal ? 'pending' : 'active') + '">' + (personal ? 'Personal' : 'Business') + '</span>';
}

function expensesHtml(expenses: any[]) {
  if (!expenses.length) return '<div class="empty-state"><i data-lucide="receipt"></i><p>No expenses yet</p></div>';
  let rows = '';
  expenses.forEach((e: any) => {
    rows += '<tr>'
      + '<td>' + formatDate(e.expense_date) + '</td>'
      + '<td>' + esc(e.category) + '</td>'
      + '<td>' + esc(e.vendor || '—') + '</td>'
      + '<td>' + srcBadge(e.payment_source) + '</td>'
      + '<td class="text-right">' + formatCurrency(e.amount, e.currency) + '</td>'
      + '<td class="row-actions"><button class="btn btn-ghost btn-sm" data-edit-exp="' + e.id + '"><i data-lucide="pencil"></i></button>'
      + '<button class="btn btn-ghost btn-sm" data-del-exp="' + e.id + '"><i data-lucide="trash-2"></i></button></td>'
      + '</tr>';
  });
  return '<div class="card"><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr>'
    + '<th>Date</th><th>Category</th><th>Vendor</th><th>Paid via</th><th class="text-right">Amount</th><th>Actions</th>'
    + '</tr></thead><tbody>' + rows + '</tbody></table></div></div></div>';
}

function capitalHtml(capital: any[]) {
  if (!capital.length) return '<div class="empty-state"><i data-lucide="landmark"></i><p>No capital entries yet</p></div>';
  let rows = '';
  capital.forEach((c: any) => {
    const auto = !!c.source_expense_id;
    rows += '<tr>'
      + '<td>' + formatDate(c.date) + '</td>'
      + '<td>' + (c.type === 'draw' ? '<span class="status status-cancelled">Draw</span>' : '<span class="status status-active">Contribution</span>') + '</td>'
      + '<td>' + esc(c.method || '—') + '</td>'
      + '<td class="text-right">' + formatCurrency(c.amount, c.currency) + '</td>'
      + '<td class="row-actions">' + (auto ? '<span class="text-muted" style="font-size:0.78rem">from expense</span>' : '<button class="btn btn-ghost btn-sm" data-del-cap="' + c.id + '"><i data-lucide="trash-2"></i></button>') + '</td>'
      + '</tr>';
  });
  return '<div class="card"><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr>'
    + '<th>Date</th><th>Type</th><th>Method</th><th class="text-right">Amount</th><th>Actions</th>'
    + '</tr></thead><tbody>' + rows + '</tbody></table></div></div></div>';
}

function openExpenseForm(expense?: any) {
  const isEdit = !!expense;
  const today = new Date().toISOString().split('T')[0];
  const catOpts = CATEGORIES.map(c => '<option value="' + esc(c) + '"' + (expense?.category === c ? ' selected' : '') + '>' + esc(c) + '</option>').join('');
  openModal(isEdit ? 'Edit Expense' : 'New Expense',
    '<form id="expense-form">'
    + '<div class="form-row"><div class="form-group"><label>Date *</label><input type="date" name="expense_date" value="' + esc(expense?.expense_date?.split('T')[0] || today) + '" required></div>'
    + '<div class="form-group"><label>Amount (USD) *</label><input type="number" name="amount" step="0.01" min="0.01" value="' + esc(expense?.amount ?? '') + '" required></div></div>'
    + '<div class="form-row"><div class="form-group"><label>Category *</label><select name="category" required>' + catOpts + '</select></div>'
    + '<div class="form-group"><label>Vendor</label><input type="text" name="vendor" value="' + esc(expense?.vendor || '') + '" placeholder="Anthropic, Cloudflare, ..."></div></div>'
    + '<div class="form-group"><label>Paid via *</label><select name="payment_source">'
    + '<option value="business"' + (expense?.payment_source !== 'personal' ? ' selected' : '') + '>Business account</option>'
    + '<option value="personal"' + (expense?.payment_source === 'personal' ? ' selected' : '') + '>Personal (records owner capital contribution)</option>'
    + '</select></div>'
    + '<div class="form-group"><label>Description</label><input type="text" name="description" value="' + esc(expense?.description || '') + '"></div>'
    + '<div class="form-group"><label>Notes</label><textarea name="notes" rows="2">' + esc(expense?.notes || '') + '</textarea></div>'
    + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="exp-cancel">Cancel</button>'
    + '<button type="submit" class="btn btn-primary">' + (isEdit ? 'Save' : 'Add Expense') + '</button></div></form>');
  document.getElementById('exp-cancel')?.addEventListener('click', closeModal);
  document.getElementById('expense-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    body.amount = parseFloat(body.amount);
    body.currency = 'USD';
    try {
      if (isEdit) { await api.put('/expenses/' + expense.id, body); toast('Expense updated', 'success'); }
      else { await api.post('/expenses', body); toast('Expense added', 'success'); }
      closeModal(); renderFinance();
    } catch (err: any) { toast(err.message, 'error'); }
  });
}

function openCapitalForm() {
  const today = new Date().toISOString().split('T')[0];
  openModal('New Capital Entry',
    '<form id="capital-form">'
    + '<div class="form-row"><div class="form-group"><label>Date *</label><input type="date" name="date" value="' + today + '" required></div>'
    + '<div class="form-group"><label>Amount (USD) *</label><input type="number" name="amount" step="0.01" min="0.01" required></div></div>'
    + '<div class="form-row"><div class="form-group"><label>Type *</label><select name="type">'
    + '<option value="contribution">Contribution (owner puts money in)</option>'
    + '<option value="draw">Draw (owner takes money out)</option>'
    + '</select></div>'
    + '<div class="form-group"><label>Method</label><select name="method">'
    + '<option value="wire">Wire</option><option value="cash">Cash</option><option value="crypto">Crypto</option><option value="other">Other</option>'
    + '</select></div></div>'
    + '<div class="form-group"><label>Notes</label><textarea name="notes" rows="2"></textarea></div>'
    + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="cap-cancel">Cancel</button>'
    + '<button type="submit" class="btn btn-primary">Add Entry</button></div></form>');
  document.getElementById('cap-cancel')?.addEventListener('click', closeModal);
  document.getElementById('capital-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body: any = Object.fromEntries(fd);
    body.amount = parseFloat(body.amount);
    body.currency = 'USD';
    try {
      await api.post('/capital', body); toast('Capital entry added', 'success');
      closeModal(); renderFinance();
    } catch (err: any) { toast(err.message, 'error'); }
  });
}
